/**
 * Privacy notice for the free, client-side build of Bliksys.
 *
 * Each disclosure is a native `<details>/<summary>` so the keyboard +
 * a11y semantics come for free. The visual style mirrors the
 * `SettingsSection` pattern used in Project Settings.
 *
 * The paid centralized version (when shipped) will publish its own
 * notice — this page covers ONLY the stateless localStorage build.
 */

import { useDocumentTitle } from '../hooks/useDocumentTitle.js';

import { BliksysMark } from './BliksysMark.js';
import { confirm } from './ConfirmDialog.js';
import { Icon } from './Icon.js';
import { toast } from './Toast.js';
import './Privacy.css';

interface Props {
  onBack: () => void;
}

export function Privacy({ onBack }: Props): JSX.Element {
  useDocumentTitle('Privacy — Bliksys');

  // Pre-open a targeted section when the URL carries a section id in the
  // hash (e.g. /privacy#credentials, linked from the credential notice
  // in the settings dialog). Read once at mount so user-driven toggles
  // afterwards stick. Falls back to opening the top-level "servers"
  // section when no hash or an unknown one is present, so the page
  // always has at least one section visible.
  const initialOpenSection = window.location.hash.slice(1) || 'servers';

  const handleDeleteAll = async (): Promise<void> => {
    const ok = await confirm({
      title: 'Delete all local Bliksys data?',
      message:
        'This wipes every project, every saved credential, and every UI preference Bliksys stores in this browser. Make sure you have downloaded anything you want to keep — this cannot be undone.',
      confirmLabel: 'Delete everything',
      cancelLabel: 'Cancel',
      danger: true,
    });
    if (!ok) return;
    try {
      // Match every key Bliksys owns. Iterating with a snapshot prevents
      // index drift as removeItem shrinks the store mid-loop.
      const toRemove: string[] = [];
      for (let i = 0; i < window.localStorage.length; i++) {
        const key = window.localStorage.key(i);
        if (key?.startsWith('bliksys.')) toRemove.push(key);
      }
      for (const key of toRemove) window.localStorage.removeItem(key);
      toast('All local Bliksys data deleted.', 'success');
      onBack();
    } catch {
      toast('Could not delete local data.', 'error');
    }
  };

  return (
    <div className="privacy">
      <header className="privacy-header">
        <div className="privacy-logo" aria-hidden="true">
          <BliksysMark size={32} />
        </div>
        <h1 className="privacy-title">Privacy notice</h1>
        <p className="privacy-subtitle">
          Bliksys is stateless by design. Here is exactly what that means for your data.
        </p>
        <button type="button" className="privacy-back" onClick={onBack}>
          <Icon name="arrow-left" size={16} />
          <span>Back to Bliksys</span>
        </button>
      </header>

      <section className="privacy-sections">
        <PrivacySection
          id="servers"
          title="What we collect on our servers"
          defaultOpen={initialOpenSection === 'servers'}
        >
          <p>
            <strong>Nothing.</strong> The free version of Bliksys is fully client-side. There is no
            Bliksys backend that receives, stores, or processes your data. We do not run accounts,
            analytics, or telemetry against this build.
          </p>
        </PrivacySection>

        <PrivacySection
          id="browser-storage"
          title="What is stored in your browser"
          defaultOpen={initialOpenSection === 'browser-storage'}
        >
          <p>
            Bliksys persists your work in your browser&apos;s <code>localStorage</code> under three
            keys:
          </p>
          <ul>
            <li>
              <code>bliksys.projects.v1</code> — your projects, including project name, JSON Schema,
              content document, deploy-target configuration, and asset-origin configuration.
            </li>
            <li>
              <code>bliksys.demoDismissed.v1</code> — a UI flag remembering that you dismissed the
              ACME demo card.
            </li>
            <li>
              <code>bliksys.lastOpened.v1</code> — the id of the project you most recently opened.
            </li>
          </ul>
          <p>
            No cookies are set. Clearing your browser&apos;s site data wipes everything Bliksys has
            stored.
          </p>
        </PrivacySection>

        <PrivacySection
          id="credentials"
          title="Credentials in browser storage"
          defaultOpen={initialOpenSection === 'credentials'}
        >
          <p>
            When you configure a deploy target or an asset origin you may enter access credentials:
            a GitHub personal access token, a Cloudflare KV API token, or S3-compatible access keys.
            These are stored alongside the rest of your project config in <code>localStorage</code>,
            in plaintext.
          </p>
          <p>
            They never leave your browser unless <em>you</em> trigger an action that uses them
            (Deploy, or Browse on the asset origin) — at which point they go directly to the
            third-party service you configured. Recommendation: use credentials with the minimum
            scope your workflow needs, and revoke them if your machine is compromised.
          </p>
          <p>
            The planned paid version moves credentials to a server-side secret store; this trade-
            off applies only to the free, client-side build.
          </p>
        </PrivacySection>

        <PrivacySection
          id="data-egress"
          title="When data leaves your browser"
          defaultOpen={initialOpenSection === 'data-egress'}
        >
          <p>Data only leaves your browser on actions you initiate:</p>
          <ul>
            <li>
              <strong>Deploy</strong> — sends your content (and the active schema) to the deploy
              target you configured: a webhook URL, a GitHub repository, or a similar endpoint.
              Webhook deliveries are signed with HMAC-SHA256 using your configured secret.
            </li>
            <li>
              <strong>Browse / upload assets</strong> — when you use the image-widget Browse on a
              configured S3-compatible origin, the browser calls that origin directly to list and
              upload assets.
            </li>
            <li>
              <strong>Remote schema references</strong> — if your JSON Schema declares a{' '}
              <code>$ref</code> with an <code>https://</code> URL, the engine fetches that URL to
              resolve the reference.
            </li>
          </ul>
          <p>No data leaves your browser otherwise.</p>
        </PrivacySection>

        <PrivacySection
          id="tracking"
          title="Cookies, analytics, tracking"
          defaultOpen={initialOpenSection === 'tracking'}
        >
          <p>
            None. Bliksys does not set cookies, run analytics, embed third-party scripts, or
            fingerprint your browser. There are no marketing pixels, no session replay, no error
            reporting that phones home.
          </p>
        </PrivacySection>

        <PrivacySection
          id="third-parties"
          title="Third-party services you connect"
          defaultOpen={initialOpenSection === 'third-parties'}
        >
          <p>
            Configuring a deploy target or asset origin opts you in to sending data to that
            third-party service. Examples include GitHub, your own webhook endpoint, AWS S3,
            Cloudflare R2, or Backblaze B2. Once data reaches them, their privacy policies — not
            this one — govern what happens to it.
          </p>
          <p>
            We have no relationship with these services on your behalf. You configure them, you
            authenticate to them, you control them.
          </p>
        </PrivacySection>

        <PrivacySection
          id="future-paid"
          title="The future paid version"
          defaultOpen={initialOpenSection === 'future-paid'}
        >
          <p>
            A team-oriented paid version is planned. It will store project data on Bliksys-operated
            servers, require accounts, and be governed by a separate privacy notice that will be
            published before it ships.
          </p>
          <p>
            <strong>This document covers only the free, client-side build.</strong> If you are using
            a hosted Bliksys instance under a different domain, check that instance&apos;s own
            privacy notice.
          </p>
        </PrivacySection>
      </section>

      <section className="privacy-danger" aria-labelledby="privacy-danger-heading">
        <h2 id="privacy-danger-heading" className="privacy-danger-title">
          Delete all local data
        </h2>
        <p className="privacy-danger-message">
          Wipe every project, every saved credential, and every UI preference Bliksys has stored in
          this browser. Anything you have not exported or deployed will be lost.
        </p>
        <button
          type="button"
          className="privacy-danger-button"
          onClick={() => void handleDeleteAll()}
        >
          Delete all local data
        </button>
      </section>

      <p className="privacy-footnote">Last updated: 2026-05-01.</p>
    </div>
  );
}

/**
 * Collapsible section. Native `<details>` so keyboard navigation, screen
 * reader semantics, and click-to-expand work without JS. Visual style
 * mirrors `SettingsSection` from Project Settings — same caret rotation,
 * same card surface — so the page feels at home in the rest of the app.
 *
 * The `id` is also the URL hash slug — `<a href="/privacy#credentials">`
 * scrolls the browser to this section AND the parent uses the hash to
 * pre-open the targeted one.
 */
function PrivacySection({
  id,
  title,
  defaultOpen = false,
  children,
}: {
  id: string;
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <details id={id} className="privacy-section" open={defaultOpen}>
      <summary className="privacy-section-summary">
        <span className="privacy-section-title">{title}</span>
      </summary>
      <div className="privacy-section-body">{children}</div>
    </details>
  );
}
